
import { useState } from "react";
import { User } from "@supabase/supabase-js";
import { Camera, UserRound } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog } from "@/components/ui/dialog";
import { useProfilePicture } from "./picture-upload/useProfilePicture";
import { UploadDialog } from "./picture-upload/UploadDialog";
import { useProfile } from "@/hooks/useProfile";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProfilePictureUploadProps {
  user: User;
}

export function ProfilePictureUpload({ user }: ProfilePictureUploadProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { profile } = useProfile(user);
  const { data: identityAvatar } = useQuery({
    queryKey: ["identity-avatar", user.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_identity_avatar" as any);
      if (error) return null;
      return (data as string) || null;
    },
    staleTime: 60_000,
    enabled: !!user?.id && !profile?.avatar_url,
  });
  
  const handleSuccess = () => {
    // Invalidate and refetch profile data
    queryClient.invalidateQueries({ queryKey: ["profile-data", user.id] });
    setIsDialogOpen(false);
  };

  const { 
    isUploading, 
    handleFileUpload, 
    handleAvatarUrlUpdate, 
    handleRemovePicture,
    handleImportFromIdentity,
  } = useProfilePicture(user, handleSuccess);

  const avatarUrl = profile?.avatar_url || identityAvatar || undefined;

  return (
    <>
      <Avatar 
        className="h-24 w-24 cursor-pointer group relative"
        onClick={() => setIsDialogOpen(true)}
      >
        {avatarUrl ? (
          <AvatarImage 
            src={avatarUrl}
            alt={user.email || "Profile picture"}
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            onError={(e: any) => { try { e.currentTarget.style.display = 'none' } catch {} }}
          />
        ) : null}
        <AvatarFallback>
          <UserRound className="h-12 w-12 text-muted-foreground" />
        </AvatarFallback>
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
          <Camera className="h-6 w-6 text-white" />
        </div>
      </Avatar>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <UploadDialog
          isUploading={isUploading}
          hasAvatar={!!profile?.avatar_url}
          onUrlUpdate={async (url) => {
            await handleAvatarUrlUpdate(url);
          }}
          onFileUpload={handleFileUpload}
          onRemove={async () => {
            await handleRemovePicture();
          }}
          onImportFromIdentity={handleImportFromIdentity}
        />
      </Dialog>
    </>
  );
}
